import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NetworkError, signIn, startGoogleOAuth } from '../services/authService';
import { getDashboardRouteForRole, saveSessionTokens } from '../services/session';
import loginPageImage from '../assets/images/login_page.png';

type FormState = {
  email: string;
  password: string;
  companyCode: string;
  fullName: string;
  employeeId: string;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    email: '',
    password: '',
    companyCode: '',
    fullName: '',
    employeeId: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return form.email.trim().length > 0 && form.password.length > 0;
  }, [form.email, form.password]);

  const canStartGoogle = useMemo(() => {
    return (
      form.companyCode.trim().length > 0 &&
      form.fullName.trim().length > 0 &&
      form.employeeId.trim().length > 0
    );
  }, [form.companyCode, form.fullName, form.employeeId]);

  const mapError = (error: unknown): string => {
    if (error instanceof NetworkError) {
      return error.message;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (message.includes('invalid') || message.includes('credentials')) {
        return 'Invalid email or password.';
      }
      if (message.includes('email not confirmed')) {
        return 'Please verify your email before logging in.';
      }
    }

    return 'Something went wrong. Try again.';
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const validateForm = (): string | null => {
    if (!form.email.trim()) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      return 'Enter a valid email';
    }

    if (!form.password) {
      return 'Password is required';
    }

    return null;
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      const result = await signIn(form.email, form.password);
      saveSessionTokens({
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
      });
      navigate(getDashboardRouteForRole(result.role));
    } catch (error) {
      setErrorMessage(mapError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSignIn = async () => {
    if (!canStartGoogle) {
      setErrorMessage('For Google login, enter company code, employee name, and employee ID.');
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      await startGoogleOAuth({
        redirectTo: `${window.location.origin}/oauth/callback`,
        companyCode: form.companyCode,
        fullName: form.fullName,
        employeeId: form.employeeId,
      });
    } catch (error) {
      setErrorMessage(mapError(error));
      setIsLoading(false);
    }
  };

  return (
    <div
      className="h-screen w-screen bg-[#171d28]"
      style={{
        backgroundImage: 'radial-gradient(rgba(149, 175, 202, 0.28) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
      }}
    >
      <div className="h-full w-full overflow-hidden bg-white">
        <div className="grid h-full min-h-[760px] md:grid-cols-2">
          <section
            className="relative hidden overflow-hidden bg-[#0a6f84] text-white md:flex md:flex-col md:justify-between"
            style={{
              backgroundImage: `url(${loginPageImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="relative z-10 mx-auto px-10 text-center">
              <h1 className="mt-60 text-[60px] font-extrabold leading-[1.06] tracking-[-0.03em]">
                Travel with Absolute
                <br />
                Confidence.
              </h1>
              <p className="mt-40 text-[22px] font-semibold leading-[1.5] text-white/70 max-w-[500px] mx-auto">
                The next generation of enterprise expense management, fluid as the ocean and precise as a compass.
              </p>
            </div>
          </section>

          <section className="flex h-full items-center bg-[#f8fbfd] px-7 py-10 sm:px-10 md:px-16 md:py-16">
            <div className="mx-auto w-full max-w-[560px]">
              <h2 className="text-[56px] font-extrabold tracking-[-0.03em] text-[#0c5870]">Welcome Back</h2>
              <p className="mt-3 text-[28px] font-medium leading-[1] text-[#7b9aab]">Log in to your account</p>

              <form className="mt-12 space-y-7" onSubmit={onSubmit}>
                <div>
                  <label className="mb-2 block text-[14px] font-extrabold uppercase tracking-[0.16em] text-[#a9c7d9]" htmlFor="email">
                    Work Email
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleInput}
                      placeholder="name@company.com"
                      autoComplete="email"
                      className="h-[72px] w-full rounded-[18px] border-2 border-[#9cb8ca] bg-[#eaf2fb] px-7 pr-14 text-[20px] font-semibold text-[#111827] outline-none transition focus:border-[#5f9bb3]"
                    />
                    <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-[#b4c1cc]">
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <path d="M4 7l8 6 8-6" />
                      </svg>
                    </span>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-[14px] font-extrabold uppercase tracking-[0.16em] text-[#a9c7d9]" htmlFor="password">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-[14px] font-extrabold uppercase tracking-[0.1em] text-[#5b8ca3] hover:text-[#406f86]">
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={handleInput}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="h-[72px] w-full rounded-[18px] border-2 border-[#9cb8ca] bg-[#eaf2fb] px-7 pr-20 text-[20px] font-semibold text-[#111827] outline-none transition focus:border-[#5f9bb3]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-[#8ea6b6] hover:text-[#607b8d]"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 3l18 18" />
                          <path d="M10.6 10.6a3 3 0 0 0 4.2 4.2" />
                          <path d="M9.9 4.2A10.3 10.3 0 0 1 12 4c7 0 10 8 10 8a15.5 15.5 0 0 1-4 5.1" />
                          <path d="M6.6 6.6A15.3 15.3 0 0 0 2 12s3 8 10 8a10 10 0 0 0 5.4-1.5" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center gap-4 text-[20px] font-semibold text-[#6f8c9d]">
                  <input
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(event) => setKeepSignedIn(event.target.checked)}
                    className="h-7 w-7 rounded-[4px] border border-[#92afc1] accent-[#0c7a95]"
                  />
                  Keep me signed in for 30 days
                </label>

                {errorMessage ? <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] font-medium text-red-600">{errorMessage}</p> : null}

                <button
                  type="submit"
                  disabled={!canSubmit || isLoading}
                  className="inline-flex h-[74px] w-full items-center justify-center rounded-full bg-[#0c8dab] px-6 text-[24px] font-extrabold text-white shadow-[0_10px_24px_rgba(8,96,120,0.28)] transition hover:bg-[#0b7e99] disabled:cursor-not-allowed disabled:bg-[#87becc]"
                >
                  {isLoading ? 'Signing In...' : 'Login'}
                </button>

                <div className="rounded-[16px] border border-[#d5e4ed] bg-white/80 p-4">
                  <p className="mb-3 text-[12px] font-extrabold uppercase tracking-[0.12em] text-[#6e92a5]">
                    Google Login Details
                  </p>
                  <div className="grid gap-3 md:grid-cols-3">
                    <input
                      name="companyCode"
                      value={form.companyCode}
                      onChange={handleInput}
                      placeholder="Company code"
                      className="h-12 rounded-[12px] border border-[#c8dbe7] px-3 text-[14px]"
                    />
                    <input
                      name="fullName"
                      value={form.fullName}
                      onChange={handleInput}
                      placeholder="Employee name"
                      className="h-12 rounded-[12px] border border-[#c8dbe7] px-3 text-[14px]"
                    />
                    <input
                      name="employeeId"
                      value={form.employeeId}
                      onChange={handleInput}
                      placeholder="Employee ID"
                      className="h-12 rounded-[12px] border border-[#c8dbe7] px-3 text-[14px]"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onGoogleSignIn}
                  disabled={!canStartGoogle || isLoading}
                  className="inline-flex h-[60px] w-full items-center justify-center rounded-full border-2 border-[#9cb8ca] bg-white px-6 text-[20px] font-extrabold text-[#0c5870] transition hover:bg-[#eef6fb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Continue with Google
                </button>

                <p className="pt-4 text-center text-[18px] font-semibold text-[#7391a3]">
                  Don&apos;t have an account?{' '}
                  <Link to="/signup" className="font-extrabold text-[#1f8baa] hover:text-[#167690]">
                    Contact Admin
                  </Link>
                </p>
              </form>

              <div className="mt-2 border-t border-[#e1ebf1] pt-8 text-center">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#c1d2de]">Privacy Policy &nbsp;&nbsp; Terms of Service &nbsp;&nbsp; Security</p>
                <p className="mt-3 text-[11px] font-bold tracking-[0.12em] text-[#c1d2de]">© 2026 FlintPay</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
